/** "Talk to the Studio" (PRD §30.1): sticky message box + Stop. Messages mid-step queue for the Studio's next turn. */
import { useRef, useState, type KeyboardEvent } from "react";
import { Button, ConfirmDialog } from "../../components/ui";

const finePointer = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;

export function Composer({ onSend, onStop, canStop, stopping, busyHint }: { onSend: (body: string) => void; onStop: () => Promise<unknown>; canStop: boolean; stopping: boolean; busyHint?: string }) {
  const [text, setText] = useState("");
  const [confirmStop, setConfirmStop] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const send = () => {
    const body = text.trim();
    if (!body) return;
    onSend(body);
    setText("");
    ref.current?.focus();
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Desktop: Enter sends, Shift+Enter is a new line. Phones: Enter is always a new line (send with the button).
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && finePointer()) { e.preventDefault(); send(); }
  };
  return (
    <div className="composer">
      {busyHint && <p className="composer-hint muted small">{busyHint}</p>}
      <form className="composer-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
        {canStop && <Button variant="danger" icon="stop" aria-label="Stop the current step" onClick={() => setConfirmStop(true)} busy={stopping} className="composer-stop" />}
        <label htmlFor="composer-input" className="sr-only">Message the Studio</label>
        <textarea id="composer-input" ref={ref} rows={1} value={text} maxLength={8000} placeholder="Talk to the Studio…" onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 180)}px`; }} onKeyDown={onKey} />
        <Button type="submit" variant="primary" icon="send" aria-label="Send" disabled={!text.trim()} />
      </form>
      {confirmStop && (
        <ConfirmDialog title="Stop the current step?" confirmLabel="Stop" busy={stopping}
          impact="The Studio stops after its current tool call and waits for you. Nothing is lost — the conversation keeps its full context, and you can tell it how to continue."
          onCancel={() => setConfirmStop(false)} onConfirm={() => { void onStop().finally(() => setConfirmStop(false)); }} />
      )}
    </div>
  );
}
